/**
 * Pure hex-color math shared by BrandingContext's org-branding derivation
 * and the #1630 dark-mode brand-override logic (darkBrandOverride.js).
 *
 * Extracted from BrandingContext.jsx (#1630) so the derivation logic can be
 * unit-tested without a DOM (frontend vitest has no jsdom — see
 * docs/subsystems/testing.md / MEMORY reference_frontend_no_jsdom).
 * No React, no DOM — plain functions only.
 */

/**
 * Parse a hex color string to [r, g, b] array.
 * Supports #RGB, #RRGGBB formats.
 */
export function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

/**
 * Darken a hex color by a percentage (0-1).
 * Used to derive hover/dark variants from primary.
 */
export function darkenHex(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const darken = (v) => Math.round(v * (1 - amount));
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
}

/**
 * Lighten a hex color by mixing with white.
 * amount 0 = original, 1 = white.
 */
export function lightenHex(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const lighten = (v) => Math.round(v + (255 - v) * amount);
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;
}

/**
 * Mix two hex colors at a given ratio (0 = color1, 1 = color2).
 */
export function mixHex(hex1, hex2, ratio) {
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  const mix = (a, c) => Math.round(a + (c - a) * ratio);
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(mix(r1, r2))}${toHex(mix(g1, g2))}${toHex(mix(b1, b2))}`;
}

/**
 * Compute relative luminance of a hex color (0-1). WCAG 2.1 formula.
 */
export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Alpha-composite `overlayHex` over `hex` at `alpha` (0-1), per-channel.
 * Used to compute the effective color under a translucent tint layer
 * (e.g. UpvoteButton.css's WCAG-floor overlay on --color-brand-gradient) —
 * standard "over" compositing with an opaque base, so no alpha channel on
 * the result.
 */
export function blendOverlay(hex, overlayHex, alpha) {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(overlayHex);
  const blend = (a, b) => Math.round(a * (1 - alpha) + b * alpha);
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(blend(r1, r2))}${toHex(blend(g1, g2))}${toHex(blend(b1, b2))}`;
}

/**
 * WCAG contrast ratio between two hex colors (1–21).
 */
export function contrastRatio(hex1, hex2) {
  const l1 = luminance(hex1) + 0.05;
  const l2 = luminance(hex2) + 0.05;
  return l1 > l2 ? l1 / l2 : l2 / l1;
}

/**
 * Lighten `hex` in small steps until it clears `minRatio` contrast against
 * `bgHex`, capping at a near-white ceiling so it can't runaway to pure white.
 * Used to auto-derive a dark-mode-safe variant of a partner's light-mode
 * brand color (#1630 decision log item 2 — "lighten primary to a contrast
 * floor" for orgs without a hand-designed dark palette).
 */
export function ensureContrastFloor(hex, bgHex, minRatio = 4.5) {
  let candidate = hex;
  let amount = 0;
  while (contrastRatio(candidate, bgHex) < minRatio && amount < 0.9) {
    amount += 0.06;
    candidate = lightenHex(hex, amount);
  }
  return candidate;
}

/**
 * Darkening twin of `ensureContrastFloor` (#4333, D-337 option (a)): darken
 * `hex` toward black in 1% steps until it clears `minRatio` against `bgHex`.
 * Returns the input unchanged when it already passes (an org whose brand is
 * already dark keeps its exact colour). Used to derive the small-text / link
 * colour (`--brand-primary-text`) from an org's brand fill colour: the fill
 * keeps the brand colour, text gets this derived one.
 *
 * Integer step counter (not a float accumulator) so the result is exactly
 * `darkenHex(hex, n / 100)` for the smallest n that clears the floor. Black
 * (n = 100) clears any light background, so the loop always terminates; on a
 * dark `bgHex` it can never clear and returns the darkest candidate tried.
 */
export function ensureContrastFloorDarken(hex, bgHex = '#ffffff', minRatio = 4.5) {
  let candidate = hex;
  let step = 0;
  while (contrastRatio(candidate, bgHex) < minRatio && step < 100) {
    step += 1;
    candidate = darkenHex(hex, step / 100);
  }
  return candidate;
}
